// Settings > Note LLM page strings (NoteLlmView.vue). Keys are read as $t('noteLlm.<key>').
export default {
  en: {
    pageTitle: 'Note LLM - FitFoodCS',
    title: 'Note LLM',
    subtitle: 'How DeepSeek turns CS notes into standard lines for Planning: the tags and their line templates, the rules and examples it follows, and the vocabulary of the planning workbook. A save applies to the next note.',
    save: 'Save',
    saving: 'Saving…',
    discard: 'Discard changes',
    saved: 'Saved',
    savedBy: '{part}: saved by {by} at {at}',
    neverSaved: 'Defaults: nothing saved yet',
    parts: { prompt: 'Tags & rules', vocab: 'Vocabulary' },
    unsaved: 'Unsaved changes',
    leave: 'Leave without saving your changes?',
    off: 'This server has no DeepSeek key, so notes are not standardised here. The settings can still be edited.',
    errorsTitle: 'Fix these before saving:',
    reset: 'Back to the defaults',
    resetConfirm: 'Replace this part with the defaults? (Nothing is saved until you press Save.)',
    tabs: { tags: 'Tags & line templates', rules: 'Rules & examples', vocab: 'Vocabulary', prompt: 'Full prompt', try: 'Try a note' },
    kinds: {
      order: 'Orders: new / web order, reorder, add, change package',
      cancel: 'Cancel, hold, resume',
      meals: 'Meal choice',
      delivery: 'Delivery',
      bill: 'Billing and promo',
      kitchen: 'Handling and diet',
      products: 'SP PHỤ',
      drinks: 'DRINKS',
      other: 'Other',
    },
    tagsHelp: 'Each line of a standard note starts with a tag. "When to use" tells DeepSeek when to pick it; the line template says how the line is written. Segments are separated by |, and a segment whose fields are all empty is left out. Fields a template leaves out still go at the end of the line, so nothing is lost.',
    fieldsTitle: 'Fields',
    fieldHelp: {
      code: 'web order code',
      standing: '"CỐ ĐỊNH" for a lasting change',
      packages: 'packages with quantity',
      period: 'package length, e.g. 10 NGÀY TỪ 7/9',
      days: 'days (or the week, or meals by day)',
      timeslot: 'time slot',
      address: 'address',
      promo: 'promo code',
      diet: 'diet code (YCDB)',
      items: 'items / instructions',
      amount: 'amount',
      text: 'anything else',
    },
    tag: 'Tag',
    template: 'Line template',
    kind: 'Group',
    when: 'When to use (DeepSeek reads this)',
    required: 'Must have (else CS get a warning)',
    addTag: 'Add a tag',
    removeTag: 'Remove this tag',
    fallbackLocked: 'Tags DeepSeek invents become this one, so it stays',
    samples: 'Recent notes written with these templates',
    samplesNone: 'No standardised notes of this group yet.',
    changed: 'changed',
    rulesTitle: 'Rules',
    rulesHelp: 'What DeepSeek is told first. Add a line for any rule of yours, e.g. "MEAT-S là gói riêng".',
    examplesTitle: 'Examples',
    examplesHelp: 'A note and the JSON answer DeepSeek should give for it. Below each, the line it makes with the current templates.',
    exampleNote: 'Note',
    exampleAnswer: 'Answer (JSON)',
    addExample: 'Add an example',
    removeExample: 'Remove',
    fixedTitle: 'Fixed part: the fields of the answer (the code reads them)',
    promptTitle: 'What DeepSeek gets with every note',
    promptHelp: 'The same for every note until the next save: the rules, the tags with "when to use", the fixed fields, the vocabulary and the examples.',
    promptSaved: 'In use (saved)',
    promptDraft: 'With your changes (not saved)',
    promptChars: '{n} characters',
    copy: 'Copy',
    copied: 'Copied',
    userTitle: "Then each note's own part",
    userHelp: "After the prompt above comes the note's own part: CONTEXT (the serving week's calendar, the customer's recent packages, saved addresses, the other notes of that week) and NOTE (the note, phone numbers hidden). Never the customer's name or phone.",
    userExample: 'Example: note #{id}, as it was sent',
    userTried: 'The note you just tried',
    userReadable: 'the CONTEXT JSON is spread over lines here to read; DeepSeek gets it on one line',
    userNone: 'No note to show yet. Try one on the "Try a note" tab.',
    vocab: {
      packages: 'Packages',
      timeslots: 'Time slots',
      diets: 'Diet codes (YCDB)',
      drinks: 'Drinks',
      products: 'Side products',
      letters: 'Meal types (short letters)',
    },
    vocabHelp: {
      packages: 'From the Codes sheet. Other spellings, separated by commas, are read as the code. Default meals: the meals a day, in the meal-type letters, e.g. 1F Sáng + 1F Trưa + 1F Tối.',
      timeslots: 'From the Codes sheet. Other spellings, separated by commas, become the code.',
      diets: 'From the Codes sheet, written exactly as on the order package.',
      drinks: 'From the code.m sheet. Flavours separated by commas.',
      products: 'From the price list of the Codes sheet. DeepSeek names side products like this.',
      letters: 'The letters of mixed meals and default meals, e.g. "1F TRƯA + 1M TỐI", and what each kind of meal is.',
    },
    cols: { code: 'Code', aliases: 'Other spellings', meals: 'Default meals', note: 'Note for Planning', text: 'Text', name: 'Name', flavours: 'Flavours', letter: 'Letter', means: 'Packages', description: 'Description' },
    addRow: 'Add a row',
    removeRow: 'Remove this row',
    tryHelp: 'Runs one note through DeepSeek with the settings on this page, saved or not. Nothing is saved.',
    tryNote: 'Note',
    tryWeek: 'Serving week id (empty: this week)',
    tryPhone: "Customer's main phone (for \"như cũ\")",
    tryRun: 'Try with DeepSeek',
    trying: 'DeepSeek is working…',
    tryTags: 'Tags',
    tryUnsure: 'LLM not sure',
    tryMs: '{ms} ms',
    tryJson: 'DeepSeek\'s JSON',
    checkFailed: 'The settings could not be checked: {msg}',
  },
  vi: {
    pageTitle: 'Note LLM - FitFoodCS',
    title: 'Note LLM',
    subtitle: 'Cách DeepSeek chuyển ghi chú của CS thành các dòng chuẩn cho Planning: nhãn và mẫu dòng, hướng dẫn và ví dụ, từ vựng của file planning. Lưu xong là áp dụng cho ghi chú tiếp theo.',
    save: 'Lưu',
    saving: 'Đang lưu…',
    discard: 'Bỏ thay đổi',
    saved: 'Đã lưu',
    savedBy: '{part}: {by} lưu lúc {at}',
    neverSaved: 'Mặc định: chưa lưu lần nào',
    parts: { prompt: 'Nhãn & hướng dẫn', vocab: 'Từ vựng' },
    unsaved: 'Có thay đổi chưa lưu',
    leave: 'Rời trang mà không lưu thay đổi?',
    off: 'Máy chủ này chưa có khoá DeepSeek nên ghi chú chưa được chuẩn hoá ở đây. Vẫn sửa được cài đặt.',
    errorsTitle: 'Cần sửa trước khi lưu:',
    reset: 'Về mặc định',
    resetConfirm: 'Thay phần này bằng mặc định? (Chưa lưu cho tới khi bấm Lưu.)',
    tabs: { tags: 'Nhãn & mẫu dòng', rules: 'Hướng dẫn & ví dụ', vocab: 'Từ vựng', prompt: 'Prompt đầy đủ', try: 'Thử ghi chú' },
    kinds: {
      order: 'Đặt hàng: đơn mới / đơn web, reorder, order thêm, đổi gói',
      cancel: 'Huỷ, bảo lưu, giao lại',
      meals: 'Chọn món',
      delivery: 'Giao hàng',
      bill: 'Bill và khuyến mãi',
      kitchen: 'Dặn bếp và ăn kiêng',
      products: 'SP PHỤ',
      drinks: 'DRINKS',
      other: 'Khác',
    },
    tagsHelp: 'Mỗi dòng của ghi chú chuẩn bắt đầu bằng một nhãn. "Khi nào dùng" cho DeepSeek biết lúc nào chọn nhãn đó; mẫu dòng quy định cách viết dòng. Các phần cách nhau bằng dấu |, phần nào mọi trường đều trống thì bỏ đi. Trường nào mẫu không đặt vẫn được ghi ở cuối dòng, nên không mất thông tin.',
    fieldsTitle: 'Các trường',
    fieldHelp: {
      code: 'mã đơn web',
      standing: '"CỐ ĐỊNH" khi thay đổi lâu dài',
      packages: 'gói kèm số lượng',
      period: 'thời gian gói, vd 10 NGÀY TỪ 7/9',
      days: 'ngày (hoặc cả tuần, hoặc bữa theo ngày)',
      timeslot: 'khung giờ',
      address: 'địa chỉ',
      promo: 'mã khuyến mãi',
      diet: 'yêu cầu ăn kiêng (YCDB)',
      items: 'món / dặn dò',
      amount: 'số tiền',
      text: 'phần còn lại',
    },
    tag: 'Nhãn',
    template: 'Mẫu dòng',
    kind: 'Nhóm',
    when: 'Khi nào dùng (DeepSeek đọc phần này)',
    required: 'Bắt buộc có (thiếu thì CS thấy cảnh báo)',
    addTag: 'Thêm nhãn',
    removeTag: 'Xoá nhãn này',
    fallbackLocked: 'Nhãn DeepSeek tự đặt ra sẽ thành nhãn này, nên phải giữ',
    samples: 'Ghi chú gần đây viết theo mẫu trên',
    samplesNone: 'Chưa có ghi chú nào của nhóm này được chuẩn hoá.',
    changed: 'đã đổi',
    rulesTitle: 'Hướng dẫn',
    rulesHelp: 'Phần DeepSeek đọc đầu tiên. Thêm một dòng cho quy tắc riêng, vd "MEAT-S là gói riêng".',
    examplesTitle: 'Ví dụ',
    examplesHelp: 'Một ghi chú và JSON DeepSeek nên trả lời. Bên dưới mỗi ví dụ là dòng chuẩn theo mẫu dòng hiện tại.',
    exampleNote: 'Ghi chú',
    exampleAnswer: 'Trả lời (JSON)',
    addExample: 'Thêm ví dụ',
    removeExample: 'Xoá',
    fixedTitle: 'Phần cố định: các trường của câu trả lời (code đọc theo)',
    promptTitle: 'Prompt DeepSeek nhận với mỗi ghi chú',
    promptHelp: 'Giống nhau cho mọi ghi chú cho tới lần lưu sau: hướng dẫn, các nhãn với "khi nào dùng", phần cố định, từ vựng và ví dụ.',
    promptSaved: 'Đang dùng (đã lưu)',
    promptDraft: 'Theo thay đổi (chưa lưu)',
    promptChars: '{n} ký tự',
    copy: 'Chép',
    copied: 'Đã chép',
    userTitle: 'Sau đó là phần riêng của từng ghi chú',
    userHelp: 'Sau prompt trên là phần riêng của ghi chú: CONTEXT (lịch tuần phục vụ, các gói gần đây của khách, địa chỉ đã lưu, các ghi chú khác của tuần đó) và NOTE (nội dung ghi chú, số điện thoại đã che). Không bao giờ có tên hay số điện thoại của khách.',
    userExample: 'Ví dụ: ghi chú #{id}, như đã gửi cho DeepSeek',
    userTried: 'Ghi chú vừa thử',
    userReadable: 'ở đây JSON của CONTEXT được xuống dòng cho dễ đọc; DeepSeek nhận trên một dòng',
    userNone: 'Chưa có ghi chú để làm ví dụ. Thử một ghi chú ở tab "Thử ghi chú".',
    vocab: {
      packages: 'Gói ăn',
      timeslots: 'Khung giờ',
      diets: 'Yêu cầu ăn kiêng (YCDB)',
      drinks: 'Nước uống',
      products: 'Sản phẩm thêm',
      letters: 'Loại meal (chữ viết tắt)',
    },
    vocabHelp: {
      packages: 'Lấy từ sheet Codes. Cách viết khác (cách nhau dấu phẩy) được hiểu là mã này. Bữa mặc định: các bữa mỗi ngày của gói, viết bằng chữ của loại meal, vd 1F Sáng + 1F Trưa + 1F Tối.',
      timeslots: 'Lấy từ sheet Codes. Cách viết khác (cách nhau dấu phẩy) được đổi thành mã.',
      diets: 'Lấy từ sheet Codes, viết y như trên gói của đơn.',
      drinks: 'Lấy từ sheet code.m. Các vị cách nhau dấu phẩy.',
      products: 'Lấy từ bảng giá sheet Codes. DeepSeek gọi tên sản phẩm thêm theo danh sách này.',
      letters: 'Chữ dùng trong bữa trộn và bữa mặc định, vd "1F TRƯA + 1M TỐI", và mô tả từng loại meal.',
    },
    cols: { code: 'Mã', aliases: 'Cách viết khác', meals: 'Bữa mặc định', note: 'Ghi chú cho Planning', text: 'Nội dung', name: 'Tên', flavours: 'Các vị', letter: 'Chữ', means: 'Gói', description: 'Mô tả' },
    addRow: 'Thêm dòng',
    removeRow: 'Xoá dòng này',
    tryHelp: 'Chạy một ghi chú qua DeepSeek với cài đặt trên trang này, đã lưu hay chưa. Không lưu gì.',
    tryNote: 'Ghi chú',
    tryWeek: 'Mã tuần phục vụ (để trống: tuần này)',
    tryPhone: 'SĐT chính của khách (cho "như cũ")',
    tryRun: 'Thử với DeepSeek',
    trying: 'DeepSeek đang xử lý…',
    tryTags: 'Nhãn',
    tryUnsure: 'LLM chưa chắc',
    tryMs: '{ms} ms',
    tryJson: 'JSON của DeepSeek',
    checkFailed: 'Không kiểm tra được cài đặt: {msg}',
  },
}
